// src/components/KingCritChat.js
// King Crit — AI Onboarding Concierge Chat Widget
// Floating chat bubble that expands into a chat panel.
// Works pre-auth (stateless) and post-auth (conversation persisted).

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
  StyleSheet,
  Animated,
  Dimensions,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const AVATAR = require('../../assets/Crit_Mascot_Transparent.webp');
const GOLD = '#FCD000';
const BG_DARK = '#0d0d0e';
const BG_PANEL = '#1a1a1c';
const BG_INPUT = '#2a2a2e';
const BG_USER_BUBBLE = '#2a2a2e';
const BG_BOT_BUBBLE = '#1f1f22';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SECONDARY = '#999';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "Yo, what's good! I'm King Crit, your guide to CritCast. Whether you wanna know how to go live, how events work, or how to get started, I got you. What's on your mind? 👑",
};

const QUICK_ACTIONS = [
  { label: 'How does it work?', message: 'How does CritCast work?' },
  { label: 'Is this legit?', message: 'Is CritCast legit? What\'s the catch?' },
  { label: 'What games can I play?', message: 'What games can I play on CritCast right now?' },
  { label: 'What is the creator program?', message: 'What is the creator program and how does it work?' },
];

// ---------------------------------------------------------------------------
// Typing Indicator (bouncing dots)
// ---------------------------------------------------------------------------
function TypingIndicator() {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animate = (dot, delay) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, { toValue: -6, duration: 300, useNativeDriver: true }),
          Animated.timing(dot, { toValue: 0, duration: 300, useNativeDriver: true }),
        ])
      );
    const a1 = animate(dot1, 0);
    const a2 = animate(dot2, 150);
    const a3 = animate(dot3, 300);
    a1.start();
    a2.start();
    a3.start();
    return () => { a1.stop(); a2.stop(); a3.stop(); };
  }, [dot1, dot2, dot3]);

  return (
    <View style={styles.typingRow}>
      <Image source={AVATAR} style={styles.msgAvatar} />
      <View style={styles.typingBubble}>
        {[dot1, dot2, dot3].map((dot, i) => (
          <Animated.View
            key={i}
            style={[styles.typingDot, { transform: [{ translateY: dot }] }]}
          />
        ))}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function KingCritChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [uid, setUid] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const scrollRef = useRef(null);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const sessionId = useRef(Math.random().toString(36).slice(2) + Date.now().toString(36)).current;

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsub;
  }, []);

  // Pulse animation for the bubble
  useEffect(() => {
    if (isOpen) return;
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 1.1, duration: 1000, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 1000, useNativeDriver: true }),
      ])
    );
    pulse.start();
    return () => pulse.stop();
  }, [isOpen, pulseAnim]);

  // First-visit tooltip
  useEffect(() => {
    if (Platform.OS === 'web') {
      try {
        const seen = localStorage.getItem('kingCritSeen');
        if (!seen) {
          setShowTooltip(true);
          setTimeout(() => {
            setShowTooltip(false);
            localStorage.setItem('kingCritSeen', 'true');
          }, 5000);
        }
      } catch (_) { /* localStorage unavailable */ }
    }
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 100);
    }
  }, [messages, isLoading]);

  // Send message
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isLoading) return;

    const userMsg = { role: 'user', content: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);

    try {
      const functions = getFunctions(undefined, 'us-central1');
      const callKingCrit = httpsCallable(functions, 'sendKingCritMessage');

      // Build history (exclude welcome message for API calls)
      const historyForApi = [...messages.filter(m => m !== WELCOME_MESSAGE), userMsg]
        .map(m => ({ role: m.role, content: m.content }));

      const result = await callKingCrit({
        message: trimmed,
        conversationHistory: historyForApi,
        sessionId,
        conversationId,
      });

      const { reply, conversationId: newConvId } = result.data;

      if (newConvId && !conversationId) {
        setConversationId(newConvId);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      console.error('King Crit error:', err);
      const errorMsg = err?.code === 'functions/resource-exhausted'
        ? "Easy there, champ! I need a quick breather. Hit me up again in a minute. 🔥"
        : "My bad, something went sideways. Try again in a sec!";
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, messages, conversationId, sessionId]);

  const handleQuickAction = useCallback((action) => {
    sendMessage(action.message);
  }, [sendMessage]);

  const hasUserMessages = messages.some(m => m.role === 'user');
  const windowWidth = Dimensions.get('window').width;
  const isMobile = windowWidth < 768;

  // --- Collapsed bubble ---
  if (!isOpen) {
    return (
      <View style={styles.bubbleContainer}>
        {showTooltip && (
          <View style={styles.tooltip}>
            <Text style={styles.tooltipText}>Ask King Crit!</Text>
            <View style={styles.tooltipArrow} />
          </View>
        )}
        <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
          <TouchableOpacity
            style={styles.bubble}
            onPress={() => { setIsOpen(true); setShowTooltip(false); }}
            activeOpacity={0.8}
          >
            <Image source={AVATAR} style={styles.bubbleAvatar} />
          </TouchableOpacity>
        </Animated.View>
      </View>
    );
  }

  // --- Expanded panel ---
  return (
    <View style={[styles.panel, isMobile && styles.panelMobile]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Image source={AVATAR} style={styles.headerAvatar} />
          <View>
            <Text style={styles.headerTitle}>King Crit</Text>
            <Text style={styles.headerSubtitle}>Your CritCast guide</Text>
          </View>
        </View>
        <TouchableOpacity onPress={() => setIsOpen(false)} style={styles.closeBtn}>
          <Text style={styles.closeBtnText}>✕</Text>
        </TouchableOpacity>
      </View>

      {/* Messages */}
      <ScrollView
        ref={scrollRef}
        style={styles.messagesContainer}
        contentContainerStyle={styles.messagesContent}
        keyboardShouldPersistTaps="handled"
      >
        {messages.map((msg, i) => (
          <View
            key={i}
            style={[
              styles.msgRow,
              msg.role === 'user' ? styles.msgRowUser : styles.msgRowBot,
            ]}
          >
            {msg.role === 'assistant' && (
              <Image source={AVATAR} style={styles.msgAvatar} />
            )}
            <View
              style={[
                styles.msgBubble,
                msg.role === 'user' ? styles.userBubble : styles.botBubble,
              ]}
            >
              <Text style={styles.msgText}>{msg.content}</Text>
            </View>
          </View>
        ))}

        {isLoading && <TypingIndicator />}

        {/* Quick actions — show when no user messages yet */}
        {!hasUserMessages && !isLoading && (
          <View style={styles.quickActions}>
            {QUICK_ACTIONS.map((action, i) => (
              <TouchableOpacity
                key={i}
                style={styles.quickActionBtn}
                onPress={() => handleQuickAction(action)}
                activeOpacity={0.7}
              >
                <Text style={styles.quickActionText}>{action.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Input */}
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={inputText}
            onChangeText={setInputText}
            placeholder="Ask King Crit anything..."
            placeholderTextColor={TEXT_SECONDARY}
            maxLength={1000}
            multiline={false}
            returnKeyType="send"
            onSubmitEditing={() => sendMessage(inputText)}
            editable={!isLoading}
          />
          <TouchableOpacity
            style={[styles.sendBtn, (!inputText.trim() || isLoading) && styles.sendBtnDisabled]}
            onPress={() => sendMessage(inputText)}
            disabled={!inputText.trim() || isLoading}
            activeOpacity={0.7}
          >
            <Text style={styles.sendBtnText}>→</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  // --- Bubble (collapsed) ---
  bubbleContainer: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 9999,
    alignItems: 'flex-end',
  },
  bubble: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: BG_PANEL,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: GOLD,
    shadowColor: GOLD,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    elevation: 8,
  },
  bubbleAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },

  // --- Tooltip ---
  tooltip: {
    backgroundColor: GOLD,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 8,
  },
  tooltipText: {
    color: '#000',
    fontWeight: '700',
    fontSize: 13,
  },
  tooltipArrow: {
    position: 'absolute',
    bottom: -6,
    right: 20,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: GOLD,
  },

  // --- Panel (expanded) ---
  panel: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    bottom: 20,
    right: 20,
    width: 380,
    height: 520,
    backgroundColor: BG_PANEL,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#333',
    zIndex: 9999,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 16,
    elevation: 12,
    flexDirection: 'column',
  },
  panelMobile: {
    bottom: 0,
    right: 0,
    width: '100%',
    height: '70%',
    borderRadius: 16,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },

  // --- Header ---
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
    backgroundColor: BG_DARK,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  headerTitle: {
    color: GOLD,
    fontWeight: '700',
    fontSize: 16,
  },
  headerSubtitle: {
    color: TEXT_SECONDARY,
    fontSize: 11,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BG_INPUT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtnText: {
    color: TEXT_PRIMARY,
    fontSize: 16,
    fontWeight: '600',
  },

  // --- Messages ---
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    padding: 14,
    paddingBottom: 8,
  },
  msgRow: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-end',
  },
  msgRowUser: {
    justifyContent: 'flex-end',
  },
  msgRowBot: {
    justifyContent: 'flex-start',
  },
  msgAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginRight: 8,
  },
  msgBubble: {
    maxWidth: '78%',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 14,
  },
  userBubble: {
    backgroundColor: BG_USER_BUBBLE,
    borderBottomRightRadius: 4,
  },
  botBubble: {
    backgroundColor: BG_BOT_BUBBLE,
    borderBottomLeftRadius: 4,
    borderWidth: 1,
    borderColor: '#2a2a2e',
  },
  msgText: {
    color: TEXT_PRIMARY,
    fontSize: 14,
    lineHeight: 20,
  },

  // --- Typing indicator ---
  typingRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 12,
  },
  typingBubble: {
    flexDirection: 'row',
    backgroundColor: BG_BOT_BUBBLE,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    borderBottomLeftRadius: 4,
    gap: 4,
  },
  typingDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: GOLD,
  },

  // --- Quick actions ---
  quickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
    paddingLeft: 36, // align with bot messages (avatar width + margin)
  },
  quickActionBtn: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: GOLD,
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  quickActionText: {
    color: GOLD,
    fontSize: 13,
    fontWeight: '600',
  },

  // --- Input ---
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#333',
    backgroundColor: BG_DARK,
    gap: 8,
  },
  input: {
    flex: 1,
    backgroundColor: BG_INPUT,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    color: TEXT_PRIMARY,
    fontSize: 14,
    maxHeight: 40,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: GOLD,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendBtnDisabled: {
    opacity: 0.4,
  },
  sendBtnText: {
    color: '#000',
    fontSize: 18,
    fontWeight: '700',
  },
});
